import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, Table, Alert, Spinner, Button, Modal, Form, Badge, Row, Col } from 'react-bootstrap'
import {
  useAdminDatumUsers,
  useCreateAdminDatumUser,
  useUpdateAdminDatumUser,
  useDeleteAdminDatumUser
} from 'api/hooks/use-admin-datum'
import { useUpdateAdminMunicipality } from 'api/hooks/use-admin-municipalities'
import DatumTagsCard from './DatumTagsCard'
import DatumUserReportsModal from './DatumUserReportsModal'

const DATUM_ROLES = [
  { value: 'datum_user', label: 'User' },
  { value: 'datum_admin', label: 'Admin' }
]

const roleBadgeVariant = (role) => {
  const variants = {
    datum_user: 'primary',
    datum_admin: 'danger'
  }
  return variants[role] || 'secondary'
}

const roleLabel = (role) => {
  const found = DATUM_ROLES.find(r => r.value === role)
  return found ? found.label : role
}

const DatumAdminTab = ({ municipalityId, municipality }) => {
  const [showAddModal, setShowAddModal] = useState(false)
  const [reportsUser, setReportsUser] = useState(null)
  const [workspaceGuid, setWorkspaceGuid] = useState(municipality?.powerbi_workspace_guid || '')
  const [workspaceSaved, setWorkspaceSaved] = useState(false)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    datum_role: 'datum_user'
  })
  const [formError, setFormError] = useState(null)
  const savedTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current)
    }
  }, [])

  const { data: datumUsers, isLoading: usersLoading, error: usersError } = useAdminDatumUsers({
    municipalityId
  })

  const createUser = useCreateAdminDatumUser({
    municipalityId,
    options: {
      onSuccess: () => {
        setShowAddModal(false)
        setFormData({ first_name: '', last_name: '', email: '', datum_role: 'datum_user' })
        setFormError(null)
      },
      onError: (err) => {
        setFormError(err?.message || 'Failed to add user')
      }
    }
  })

  const updateUser = useUpdateAdminDatumUser({ municipalityId })
  const deleteUser = useDeleteAdminDatumUser({ municipalityId })

  const updateMunicipality = useUpdateAdminMunicipality({
    id: municipalityId,
    options: {
      onSuccess: () => {
        setWorkspaceSaved(true)
        if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current)
        savedTimeoutRef.current = setTimeout(() => setWorkspaceSaved(false), 3000)
      }
    }
  })

  const handleSaveWorkspace = () => {
    updateMunicipality.mutate({ powerbi_workspace_guid: workspaceGuid })
  }

  const handleAddUser = (e) => {
    e.preventDefault()
    setFormError(null)
    createUser.mutate(formData)
  }

  const handleRemoveUser = (userId) => {
    if (window.confirm('Remove this user from the Reports app?')) {
      deleteUser.mutate(userId)
    }
  }

  const handleRoleChange = (userId, newRole) => {
    updateUser.mutate({ userId, params: { datum_role: newRole } })
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setFormData({ first_name: '', last_name: '', email: '', datum_role: 'datum_user' })
    setFormError(null)
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Power BI Configuration</h5>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Power BI Workspace GUID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={workspaceGuid}
                  onChange={(e) => setWorkspaceGuid(e.target.value)}
                />
                <Form.Text className="text-muted">
                  The workspace ID from the Power BI service URL.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button
                variant="primary"
                onClick={handleSaveWorkspace}
                disabled={updateMunicipality.isLoading}
              >
                {updateMunicipality.isLoading ? 'Saving...' : 'Save'}
              </Button>
              {workspaceSaved && (
                <span className="ms-2 text-success small">Saved!</span>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Datum Users</h5>
          <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
            Add User
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {usersLoading && (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {usersError && (
            <div className="p-3">
              <Alert variant="danger" className="mb-0">Failed to load datum users.</Alert>
            </div>
          )}

          {!usersLoading && !usersError && (
            datumUsers && datumUsers.length > 0 ? (
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {datumUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => setReportsUser(user)}
                          title="View reports this user can access"
                        >
                          {user.first_name} {user.last_name}
                        </Button>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={user.datum_role || ''}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          style={{ width: 'auto' }}
                        >
                          {DATUM_ROLES.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => setReportsUser(user)}
                        >
                          Reports
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={deleteUser.isLoading}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="p-4">
                <Alert variant="info" className="mb-0">
                  No users have been granted access to the Reports (Datum) app.
                </Alert>
              </div>
            )
          )}
        </Card.Body>
      </Card>

      <DatumTagsCard municipalityId={municipalityId} />

      <DatumUserReportsModal
        municipalityId={municipalityId}
        user={reportsUser}
        show={!!reportsUser}
        onHide={() => setReportsUser(null)}
      />

      <Modal show={showAddModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Datum User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddUser}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="user@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Form.Text className="text-muted">
                If the user already exists in the municipality, their datum access will be enabled.
                Otherwise, provide a name to create a new user.
              </Form.Text>
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="First name (required for new users)"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Last name (required for new users)"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Datum Role</Form.Label>
              <Form.Select
                value={formData.datum_role}
                onChange={(e) => setFormData({ ...formData, datum_role: e.target.value })}
                required
              >
                {DATUM_ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light-primary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createUser.isLoading}
            >
              {createUser.isLoading ? 'Adding...' : 'Add User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

DatumAdminTab.propTypes = {
  municipalityId: PropTypes.string.isRequired,
  municipality: PropTypes.shape({
    powerbi_workspace_guid: PropTypes.string
  })
}

export default DatumAdminTab
